import {useMutation, useQuery} from '@apollo/client'
import {AutoSave} from '@flinkit/ra-form-layout'
import {
  AccordionDetails,
  AccordionSummary,
  AlertColor,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Grid,
  Grid2,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {isAfter} from 'date-fns'
import gql from 'graphql-tag'
import {camelCase} from 'lodash-es'
import {
  FC,
  FormEventHandler,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  AutocompleteInput,
  BooleanInput, ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin'
import {FieldValues, useFormContext, useWatch} from 'react-hook-form'
import {QRCode} from 'react-qrcode-logo'

import Accordion from '../../components/Accordion'
import AccordionWrapper from '../../components/AccordionWrapper'
import {
  ChatMessageTemplatePreview,
} from '../../components/chatMessageTemplate/ChatMessageTemplateEditor'
import DialogCloseButton from '../../components/DialogCloseButton'
import ExtendedAlert from '../../components/ExtendedAlert'
import ExtendedDateTimeInput from '../../components/ExtendedDateTimeInput'
import ExtendedDialog from '../../components/ExtendedDialog'
import ExtendedEdit from '../../components/ExtendedEdit'
import FileUploadButton from '../../components/FileUploadButton'
import {ArrowDownIcon, DownloadIcon, ShareIcon} from '../../components/icons'
import SubtitledInput from '../../components/SubtitledInput'
import TabPanel from '../../components/TabPanel'
import WhatsappAccountBusinessProfileEdit
  from '../../components/WhatsappAccountBusinessProfileEdit'
import {SandboxContext} from '../../contexts/SandboxContext'
import useHasPermission from '../../hooks/useHasPermission'
import useIsDesktop from '../../hooks/useIsDesktop'
import useQueryParameters from '../../hooks/useQueryParameters'
import {Edit as ChannelChatBotUserEdit} from '../../resources/channelChatBotUsersResource'
import {ChannelEdit, ChannelMerchantUsersShow} from '../../resources/channelsResource'
import TimeInput, {ValidationFunction} from '../../resources/components/TimeInput'
import theme from '../../theme'
import {
  ChannelChatBotUsers,
  Channels,
  ChannelWorkingHours,
  ChatMessageChatBotAutoreplyTypesEnum,
  MutationRoot,
  QueryRoot,
  WhatsappAccounts,
} from '../../types/graphqlSchema'
import {
  isTimestampAfter,
  isTimestampInPast,
  isValidDateTimeString,
} from '../../utils/dates'
import {isNativeMobileApp} from '../../utils/platform'

const ChannelSettingsDialog: FC<ChannelSettingsDialogProps> = ({
  channelId,
  onClose,
  open = false,
}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const {isSandbox} = useContext(SandboxContext)
  const [settingsTabIndex, setSettingsTabIndex] = useState(0)
  const onSettingsTabChange = (_, newSettingsTabIndex) => {
    setSettingsTabIndex(newSettingsTabIndex)
  }
  const settingsTabLabelAndComponents = useGetSettingsTabLabelAndComponents()
  return (
    <ExtendedDialog
      className={styles.root}
      keepMounted={false}
      maxWidth="md"
      onClose={onClose}
      open={open}
    >
      <DialogTitle>{translate('dialogs.channelSettings.title')}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Tabs
        TabIndicatorProps={{children: <span />}}
        indicatorColor="primary"
        onChange={onSettingsTabChange}
        textColor="primary"
        value={settingsTabIndex}
        variant="fullWidth"
      >
        {settingsTabLabelAndComponents.map(([name], i) => (
          <Tab key={i} label={name} />
        ))}
      </Tabs>
      <DialogContent className={styles.dialogContent}>
        {settingsTabLabelAndComponents.map(([, TabPanelComponent], i) => (
          <TabPanel isSelected={i === settingsTabIndex} key={i} value={settingsTabIndex}>
            {isSandbox ? (
              <ExtendedAlert severity="warning">
                {translate('sandboxSettings.featureNotAvailable')}
              </ExtendedAlert>
            ) : (
              <TabPanelComponent channelId={channelId} />
            )}
          </TabPanel>
        ))}
      </DialogContent>
    </ExtendedDialog>
  )
}

const AutoRepliesTabPanel: FC<HasChannelId> = ({channelId}) => {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const styles = useStyles()
  const translate = useTranslate()
  const {
    data: {channel_chat_bot_users: [channelChatBotUser] = []} = {},
    loading: isFetchingChannelChatbotUsers,
  } = useQuery<QueryRoot['channel_chat_bot_users']>(
    CHANNEL_CHAT_BOT_USER_QUERY, {skip: !channelId, variables: {channelId}}
  )
  const canEditChannel = useHasPermission('edit', 'channels')
  const canEditChannelChatbotUsers = useHasPermission('edit', 'channel_chat_bot_users')
  const pushAlert = (alert: Alert) => {
    setAlerts(a => a.find(({id}) => id === alert.id) ? a : [...a, alert])
  }
  useEffect(() => {
    setAlerts([])
    if (!(canEditChannel && canEditChannelChatbotUsers)) {
      pushAlert({
        id: 'warn-merchant-user-not-admin',
        severity: 'warning',
        text: translate('dialogs.channelSettings.autoReplies.permissionDenied'),
      })
    }
  }, [canEditChannel, canEditChannelChatbotUsers, translate])
  useEffect(() => {
    if (isFetchingChannelChatbotUsers) return
    if (!channelChatBotUser) {
      pushAlert({
        id: 'info-channel-missing-channel-chatbot-user',
        severity: 'info',
        text: (
          <>
            {translate('dialogs.channelSettings.autoReplies.noChatBotSetup')}
            <a href="mailto:info@flinkit.de">Flinkit</a>
          </>
        ),
      })
    }
  }, [channelChatBotUser, isFetchingChannelChatbotUsers, translate])
  return (
    <>
      {alerts.map(({severity, text}, i) => (
        <ExtendedAlert className={styles.alert} key={i} severity={severity}>
          {text}
        </ExtendedAlert>
      ))}
      <AccordionWrapper>
        <Accordion elevation={2} name="channel-absence-settings">
          <AccordionSummary expandIcon={<ArrowDownIcon />}>
            <Typography>
              {translate('dialogs.channelSettings.autoReplies.absence.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ChannelAbsenceSettings channelId={channelId} disabled={!canEditChannel} />
          </AccordionDetails>
        </Accordion>
        <Accordion elevation={2} name="channel-working-hours-settings">
          <AccordionSummary expandIcon={<ArrowDownIcon />}>
            <Typography>
              {translate('dialogs.channelSettings.autoReplies.outsideWorkingHours.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ChannelWorkingHoursSettings
              channelId={channelId} disabled={!canEditChannel}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion elevation={2} name="channel-welcome-settings">
          <AccordionSummary expandIcon={<ArrowDownIcon />}>
            <Typography>
              {translate('dialogs.channelSettings.autoReplies.welcome.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ChannelWelcomeSettings channelId={channelId} disabled={!canEditChannel} />
          </AccordionDetails>
        </Accordion>
        {channelChatBotUser && (
          <Accordion elevation={2} name="channel-chatBot-settings">
            <AccordionSummary expandIcon={<ArrowDownIcon />}>
              <Typography>
                {translate('dialogs.channelSettings.autoReplies.marketingOptIn.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ChannelChatBotSettings
                channelChatBotUser={channelChatBotUser}
                channelId={channelId}
                disabled={!(canEditChannel && canEditChannelChatbotUsers)}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </AccordionWrapper>
    </>
  )
}

const ChannelWorkingHoursPanel: FC<HasChannelId> = ({channelId}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const canEditChannelWorkingHours = useHasPermission('edit', 'channel_working_hours')
  const {
    data: {channel_working_hours: channelWorkingHours = []} = {},
    loading: isFetchingChannelWorkingHours,
  } = useQuery<QueryRoot['channel_working_hours']>(
    CHANNEL_WORKING_HOURS_QUERY, {skip: !channelId, variables: {channelId}}
  )
  if (isFetchingChannelWorkingHours) return <CircularProgress size={20} />
  return (
    <div className={styles.channelWorkingHoursPanelRoot}>
      {!canEditChannelWorkingHours && (
        <ExtendedAlert className={styles.alert} severity="warning">
          {translate('dialogs.channelSettings.workingHours.forbidden')}
        </ExtendedAlert>
      )
      }
      <Typography variant="subtitle2">
        {translate('dialogs.channelSettings.workingHours.title')}
      </Typography>
      <Typography color="textSecondary" variant="body2">
        {translate('dialogs.channelSettings.workingHours.subtitle')}
      </Typography>
      {channelWorkingHours.map(c => (
        <ExtendedEdit
          className={styles.channelWorkingHoursEdit}
          id={c.id}
          key={c.id}
          redirect={false}
          resource="channel_working_hours"
        >
          <SimpleForm resetOptions={{keepDirtyValues: false}} mode="all" toolbar={false}>
            {/* @ts-ignore */}
            <WorkingHoursInputs disabled={!canEditChannelWorkingHours} />
          </SimpleForm>
        </ExtendedEdit>
      ))}
    </div>
  )
}

const WorkingHoursInputs: FC<WorkingHoursInputsProps> = ({disabled}) => {
  const styles = useStyles()
  const record = useRecordContext()
  const [isOpen, setIsOpen] = useState(record.isOpen)
  const translate = useTranslate()
  const isDesktop = useIsDesktop()
  const {closingTime, openingTime} = useFormContext().watch()
  const daysOfTheWeek = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  ].map(d => ([
    translate(isDesktop ? `weekdays.full.${d}` : `weekdays.shortened.${d}`),
  ]))
  const validateOpeningTime: ValidationFunction = (
    value: string, {closingTime} = {}, {format} = {}
  ) => {
    const valueDate = format(value)
    const closingTimeDate = format(closingTime)
    if (value && closingTime && isAfter(valueDate, closingTimeDate)) {
      return translate('validations.time')
    }
  }
  const validateClosingTime: ValidationFunction = (
    value: string, {openingTime} = {}, {format} = {}
  ) => {
    const valueDate = format(value)
    const openingTimeDate = format(openingTime)
    if (value && openingTime && isAfter(openingTimeDate, valueDate)) {
      return translate('validations.time')
    }
  }
  return (
    <Grid2
      alignItems="center"
      className={styles.workingHoursInputs}
      container
      width="100%"
    >
      {(!isOpen || (openingTime && closingTime)) && (
        <AutoSave
          confirmationDuration={false}
          debounce={1000}
          typographyProps={{display: 'none'}}
        />
      )}
      <Grid2 size={{xs: 2}}>
        <Typography variant="h5">{daysOfTheWeek[record.day]}</Typography>
      </Grid2>
      <Grid2 size={{xs: 2}}>
        <BooleanInput
          label=""
          onChange={event => setIsOpen(event.target.checked)}
          readOnly={disabled}
          source="isOpen"
        />
      </Grid2>
      <Grid2 style={isOpen ? {display: 'none'} : {}} size={{xs: 8}}>
        <Typography align="center" variant="body2">
          {translate('dialogs.channelSettings.workingHours.closed')}
        </Typography>
      </Grid2>
      <Grid2
        className={styles.inputGrid}
        size={{xs: 4}}
        style={isOpen ? {} : {display: 'none'}}
      >
        <TimeInput
          InputProps={{isRequired: true}}
          className={styles.timeInput}
          disabled={disabled}
          label={translate('dialogs.channelSettings.workingHours.from')}
          source="openingTime"
          validate={validateOpeningTime}
        />
      </Grid2>
      <Grid2
        className={styles.inputGrid}
        size={{xs: 4}}
        style={isOpen ? {} : {display: 'none'}}
      >
        <TimeInput
          InputProps={{isRequired: true}}
          className={styles.timeInput}
          disabled={disabled}
          label={translate('dialogs.channelSettings.workingHours.to')}
          source="closingTime"
          validate={validateClosingTime}
        />
      </Grid2>
    </Grid2>
  )
}

const GeneralSettingsPanel: FC<HasChannelId> = ({channelId}) => (
  <>
    <ChannelEdit channelId={channelId} />
    {/*
     TODO: Enable on mobile once NSOSStatusErrorDomain error on downloading QR code blob
     https://github.com/ionic-team/capacitor/issues/5478
     */}
    {!isNativeMobileApp && <ChannelWhatsappQrCode channelId={channelId} />}
    <Typography variant="subtitle2">{
      useTranslate()('dialogs.channelSettings.general.membersListTitle')
    }</Typography>
    <ChannelMerchantUsersShow channelId={channelId} />
  </>
)

const ChannelWhatsappAccountBusinessProfileEditPanel: FC<HasChannelId> = ({
  channelId,
}) => {
  const {
    data: {whatsappAccountId} = {},
  } = useGetOne<Channels>('channels', {id: channelId})
  return (
    <WhatsappAccountBusinessProfileEdit id={whatsappAccountId} />
  )
}

const WhatsappChatImportPanel: FC<HasChannelId> = ({channelId}) => {
  const notify = useNotify()
  const redirect = useRedirect()
  const [textFileMerchantUserName, setTextFileMerchantUserName] = useState<string>()
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState<string>()
  const [file, setFile] = useState<FileAttachment>()
  const [
    importWhatsappChat, {loading: isLoading},
  ] = useMutation<MutationRoot['importWhatsappChat']>(
    IMPORT_WHATSAPP_CHAT_MUTATION,
    {
      onCompleted: ({importWhatsappChat: {id} = {}}) => {
        notify("Chat erfolgreich aus WhatsApp importiert", {type: 'success'})
        redirect(`/inbox/${channelId}/${id}`)
      },
      onError: e => notify(e.message, {type: 'error'}),
    }
  )
  const submit = useCallback<FormEventHandler<HTMLFormElement>>(
    e => {
      e.preventDefault()
      if (isLoading) return
      if (!(/^\+\d+$/).test(customerPhoneNumber ?? '')) {
        return notify("Ungültige Telefonnummer des Kunden", {type: 'error'})
      }
      importWhatsappChat({
        variables: {
          channelId,
          customerPhoneNumber,
          textFileContentHex: file?.encodedContent,
          textFileMerchantUserName,
        },
      })
    },
    [
      channelId,
      textFileMerchantUserName,
      customerPhoneNumber,
      file?.encodedContent,
      importWhatsappChat,
      isLoading,
      notify,
    ]
  )
  return (
    <form className={useStyles().chatImportPanel} onSubmit={submit}>
      <Typography color="textSecondary" variant="body1">
        Es werden nur WhatsApp-Chats unterstützt, die von einem deutsch-sprachigen
        iOS-Gerät exportiert wurden.
      </Typography>
      <Box>
        <FileUploadButton
          color="secondary"
          encoding="hex"
          loading={false}
          mimeTypes={["text/plain"]}
          onSelectFiles={([f]) => setFile(f)}
          variant="outlined"
        >
          WhatsApp Export-Datei auswählen
        </FileUploadButton>
        <Typography variant="body2">{file?.filename}</Typography>
      </Box>
      <TextField
        label="WhatsApp-Telefonnummer des Kunden"
        onChange={e => setCustomerPhoneNumber(e.target.value)}
        required
        value={customerPhoneNumber}
        variant="filled"
      />
      <TextField
        label="Chat file merchant user name"
        onChange={e => setTextFileMerchantUserName(e.target.value)}
        required
        value={textFileMerchantUserName}
        variant="filled"
      />
      <Button color="primary" disabled={isLoading} type="submit" variant="contained">
        Importieren
      </Button>
    </form>
  )
}

const ChannelWhatsappQrCode: FC<{channelId: string}> = ({channelId}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const qrCodeRef = useRef<any>(null)
  const imageLinkRef = useRef<HTMLAnchorElement>(null)
  const {
    data: {whatsapp_accounts: [whatsappAccount] = []} = {},
    loading: isLoadingWhatsappAccount,
  } = useQuery<WhatsappAccounts[]>(
    WHATSAPP_ACCOUNTS_QUERY, {skip: !channelId, variables: {channelId}}
  )
  const whatsappUrl = `https://wa.me/${encodeURIComponent(whatsappAccount?.phoneNumber)}`
  const downloadQrCodeImage = useCallback(() => {
    const canvasRef = qrCodeRef?.current?.canvas
    const canvas = canvasRef?.current
    // @ts-ignore we are sure the ref is attached to the hidden `a` element
    imageLinkRef.current.href = canvas?.toDataURL()
    // @ts-ignore ditto
    imageLinkRef.current.download = whatsappAccount?.phoneNumber
    imageLinkRef.current?.click()
  }, [whatsappAccount])
  const shareViaEmail = useCallback(() => {
    const emailBody = translate(
      'dialogs.channelSettings.general.qrCode.shareViaEmailBody',
      {whatsappUrl},
    )
    window.open(`mailto:?body=${emailBody}`)
  }, [translate, whatsappUrl])
  if (isLoadingWhatsappAccount) return <CircularProgress size={20} />
  return (
    <div className={styles.channelWhatsappQrCodeRoot}>
      <Typography variant="subtitle2">
        {translate('dialogs.channelSettings.general.qrCode.title')}
      </Typography>
      <Grid container>
        <Grid item>
          <QRCode
            bgColor={theme.palette.background.paper}
            fgColor={theme.palette.primary.main}
            logoImage="../../assets/imgs/qrCodeLogo.png"
            ref={qrCodeRef}
            removeQrCodeBehindLogo
            size={150}
            value={whatsappUrl}
          />
          <a hidden href="" ref={imageLinkRef} />
        </Grid>
        <Grid item justifyContent="space-between">
          <Grid item>
            <Typography variant="body2">
              {translate('dialogs.channelSettings.general.qrCode.info')}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton color="secondary" onClick={downloadQrCodeImage}>
              <DownloadIcon />
            </IconButton>
            <IconButton color="secondary" onClick={shareViaEmail}>
              <ShareIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

const ChannelAbsenceSettings: FC<HasChannelId & HasDisabled> = ({
  channelId,
  disabled,
}) => {
  const styles = useStyles()
  const translate = useTranslate()
  const validate = ({
    absenceEndTimestamp, absenceStartTimestamp, isAbsenceAutoreplyEnabled,
  }: FieldValues) => {
    if (!isAbsenceAutoreplyEnabled) return {}
    const isValidStartTimestamp = isValidDateTimeString(absenceStartTimestamp)
    const isValidEndTimestamp = isValidDateTimeString(absenceEndTimestamp)
    if (!(isValidStartTimestamp && isValidEndTimestamp)) return {
      absenceEndTimestamp: isValidEndTimestamp ?
        undefined :
        translate('dialogs.channelSettings.autoReplies.absence.invalidEndDate'),
      absenceStartTimestamp: isValidStartTimestamp ?
        undefined :
        translate('dialogs.channelSettings.autoReplies.absence.invalidEndDate'),
    }
    return {
      absenceEndTimestamp: isTimestampAfter(absenceEndTimestamp, absenceStartTimestamp) ?
        undefined :
        translate('dialogs.channelSettings.autoReplies.absence.invalidDatesOrder'),
    }
  }
  return (
    <ExtendedEdit
      id={channelId}
      redirect={false}
      resource="channels"
    >
      <SimpleForm
        defaultValues={{
          absenceAutoreplyText:
            translate('dialogs.channelSettings.autoReplies.absence.initialValue'),
        }}
        resetOptions={{keepDirtyValues: true}}
        toolbar={(
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        )}
        validate={validate}
      >
        <Box className={styles.autoreplyToggle}>
          <BooleanInput
            label={translate('dialogs.channelSettings.autoReplies.absence.toggleLabel')}
            readOnly={disabled}
            source="isAbsenceAutoreplyEnabled"
          />
        </Box>
        <ChannelAbsenceDateTimeInputs disabled={disabled} />
        <ChannelChatBotSettingsMessageInputs
          autoreplyType={ChatMessageChatBotAutoreplyTypesEnum.Absence}
          disabled={disabled}
        />
      </SimpleForm>
    </ExtendedEdit>
  )
}

const ChannelAbsenceDateTimeInputs: FC<Partial<HasDisabled>> = ({
  disabled = false,
}) => {
  const styles = useStyles()
  const notify = useNotify()
  const {setValue, watch} = useFormContext()
  const {absenceEndTimestamp, absenceStartTimestamp, isAbsenceAutoreplyEnabled} = watch()
  const {endDateInputRef, startDateInputRef} = useDateRangeInputValidation({
    endTimestamp: absenceEndTimestamp, startTimestamp: absenceStartTimestamp,
  })
  useEffect(() => {
    if (!absenceEndTimestamp) return
    // Automatically disable absence if the absence duration is expired
    if (isTimestampInPast(absenceEndTimestamp) && isAbsenceAutoreplyEnabled) {
      notify('dialogs.channelSettings.autoReplies.absence.expired')
      setValue('isAbsenceAutoreplyEnabled', false)
    }
  }, [absenceEndTimestamp, isAbsenceAutoreplyEnabled, notify, setValue])
  return (
    <Grid className={styles.channelAbsenceDateTimeInputsRoot} container width="100%">
      <Grid item xs={6}>
        <ExtendedDateTimeInput
          inputRef={startDateInputRef as RefObject<HTMLInputElement>}
          label={useTranslate()('dialogs.channelSettings.autoReplies.absence.startDate')}
          parse={dateString => new Date(dateString).toISOString()}
          readOnly={disabled}
          source="absenceStartTimestamp"
        />
      </Grid>
      <Grid item xs={6}>
        <ExtendedDateTimeInput
          inputRef={endDateInputRef as RefObject<HTMLInputElement>}
          label={useTranslate()('dialogs.channelSettings.autoReplies.absence.endDate')}
          parse={dateString => new Date(dateString).toISOString()}
          readOnly={disabled}
          source="absenceEndTimestamp"
        />
      </Grid>
    </Grid>
  )
}

const ChannelWelcomeSettings = ({channelId, disabled}) => {
  const styles = useStyles()
  return (
    <ExtendedEdit
      id={channelId}
      redirect={false}
      resource="channels"
    >
      <SimpleForm
        defaultValues={{
          welcomeAutoreplyText: useTranslate()(
            'dialogs.channelSettings.autoReplies.welcome.initialValue'
          ),
        }}
        resetOptions={{keepDirtyValues: true}}
        toolbar={
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        }
      >
        <Box className={styles.autoreplyToggle}>
          <BooleanInput
            label={useTranslate()(
              'dialogs.channelSettings.autoReplies.welcome.toggleLabel'
            )}
            readOnly={disabled}
            source="isWelcomeAutoreplyEnabled"
          />
        </Box>
        <ChannelChatBotSettingsMessageInputs
          autoreplyType={ChatMessageChatBotAutoreplyTypesEnum.Welcome}
          disabled={disabled}
        />
      </SimpleForm>
    </ExtendedEdit>
  )
}

const ChannelChatBotSettings: FC<ChannelChatBotSettingsProps> = ({
  channelChatBotUser, channelId, disabled,
}) => {
  const styles = useStyles()
  return (
    <ExtendedEdit id={channelId} redirect={false} resource="channels">
      <SimpleForm
        resetOptions={{keepDirtyValues: true}}
        toolbar={(
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        )}
      >
        <Box className={styles.autoreplyToggle}>
          <BooleanInput
            label={useTranslate()(
              'dialogs.channelSettings.autoReplies.marketingOptIn.toggleLabel'
            )}
            readOnly={disabled}
            source="isMarketingOptInAutoreplyEnabled"
          />
        </Box>
        {/* Nesting the chat bot edit component here b/c its logically
              and visually part of the outer edit form
          */}
        <ChannelChatBotUserEdit disabled={disabled} id={channelChatBotUser.id} />
      </SimpleForm>
    </ExtendedEdit>
  )
}

const ChannelWorkingHoursSettings: FC<HasChannelId & HasDisabled> = ({
  channelId, disabled,
}) => {
  const styles = useStyles()
  return (
    <ExtendedEdit
      id={channelId}
      redirect={false}
      resource="channels"
    >
      <SimpleForm
        defaultValues={{
          outsideWorkingHoursAutoreplyText: useTranslate()(
            'dialogs.channelSettings.autoReplies.outsideWorkingHours.initialValue'
          ),
        }}
        resetOptions={{keepDirtyValues: true}}
        toolbar={(
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        )}
      >
        <Box className={styles.autoreplyToggle}>
          <BooleanInput
            label={useTranslate()(
              'dialogs.channelSettings.autoReplies.outsideWorkingHours.toggleLabel'
            )}
            readOnly={disabled}
            source="isOutsideWorkingHoursAutoreplyEnabled"
          />
        </Box>
        <ChannelChatBotSettingsMessageInputs
          autoreplyType={ChatMessageChatBotAutoreplyTypesEnum.OutsideWorkingHours}
          disabled={disabled}
        />
      </SimpleForm>
    </ExtendedEdit>
  )
}

const ChannelChatBotSettingsMessageInputs: FC<{
  autoreplyType: ChatMessageChatBotAutoreplyTypesEnum
  disabled: boolean | undefined
}> = ({autoreplyType, disabled}) => {
  const autoreplyTypePrefix = {
    [ChatMessageChatBotAutoreplyTypesEnum.Absence]: 'absenceAutoreply',
    [ChatMessageChatBotAutoreplyTypesEnum.MarketingOptIn]: 'optInTriggerAutoresponse',
    [ChatMessageChatBotAutoreplyTypesEnum.MarketingOptOut]: 'optOutTriggerAutoresponse',
    [ChatMessageChatBotAutoreplyTypesEnum.OutsideWorkingHours]:
      'outsideWorkingHoursAutoreply',
    [ChatMessageChatBotAutoreplyTypesEnum.Welcome]: 'welcomeAutoreply',
  }[autoreplyType]
  const autoreplyTextSource = `${autoreplyTypePrefix}Text`
  const autoreplyMessageTypeSource = `${autoreplyTypePrefix}MessageType`
  const autoreplyChatMessageTemplateIdSource =
    `${autoreplyTypePrefix}ChatMessageTemplateId`
  const {
    [autoreplyChatMessageTemplateIdSource]: autoreplyResponseChatMessageTemplateId,
    [autoreplyMessageTypeSource]: autoreplyMessageType,
  } = useWatch()
  const {setValue} = useFormContext()
  const translate = useTranslate()
  const queryParameters = useQueryParameters()
  const isDefaultValueSet = useRef(false)
  useEffect(() => {
    if (autoreplyMessageType || isDefaultValueSet.current) return
    const timeoutId = setTimeout(
      () => {
        setValue(autoreplyMessageTypeSource, 'TEXT')
        isDefaultValueSet.current = true
      }, 0
    )
    return () => clearTimeout(timeoutId)
  }, [autoreplyMessageType, autoreplyMessageTypeSource, setValue])
  return (
    <Box width="100%">
      <SubtitledInput
        isEmptyValidationErrorMessageHidden
        title={translate(
          'dialogs.channelSettings.autoReplies.chatBotSettingsMessageInputs.title'
        )}
      >
        <SelectInput
          choices={AUTOREPLY_RESPONSE_TYPES.map(type => ({key: type, name: type}))}
          label=""
          optionValue="key"
          optionText="name"
          defaultValue="TEXT"
          emptyValue="TEXT"
          validate={required()}
          source={autoreplyMessageTypeSource}
        />
      </SubtitledInput>
      {autoreplyMessageType === 'TEXT' && (
        <TextInput
          multiline
          readOnly={disabled}
          rows={5}
          label={translate(
            `dialogs.channelSettings.autoReplies.${camelCase(autoreplyType)}.` +
            'textInputLabel'
          )}
          source={autoreplyTextSource}
        />
      )}
      {autoreplyMessageType === 'TEMPLATE' && (
        <div>
          <SubtitledInput title="">
            <ReferenceInput
              filter={{
                isDeleted: false,
                isListed: true,
                status: 'APPROVED',
                type: 'MARKETING',
              }}
              perPage={false as any}
              reference="chat_message_templates"
              source={autoreplyChatMessageTemplateIdSource}
            >
              <AutocompleteInput
                label={autoreplyResponseChatMessageTemplateId ?
                  false :
                  translate('dialogs.channelSettings.autoReplies.' +
                    'chatBotSettingsMessageInputs.templateLabel'
                  )
                }
                defaultValue={queryParameters.templateId}
                filterToQuery={q => ({'name@_ilike': q})}
              />
            </ReferenceInput>
          </SubtitledInput>
          {autoreplyResponseChatMessageTemplateId && (
            <ChatMessageTemplatePreview
              chatMessageTemplateId={autoreplyResponseChatMessageTemplateId}
            />
          )}
        </div>
      )}
    </Box>
  )
}

const useDateRangeInputValidation = ({
  endTimestamp, startTimestamp,
}: UseDateRangeInputValidationProps) => {
  const startDateInputRef = useRef<{max: string, min: string}>()
  const endDateInputRef = useRef<{max: string, min: string}>()
  useEffect(() => {
    if (!endTimestamp || !isValidDateTimeString(endTimestamp)) return
    startDateInputRef.current &&
      (startDateInputRef.current.max = new Date(endTimestamp).toISOString().slice(0, 16))
  }, [endTimestamp])
  useEffect(() => {
    if (!startTimestamp || !isValidDateTimeString(startTimestamp)) return
    endDateInputRef.current &&
      (endDateInputRef.current.min = new Date(startTimestamp).toISOString().slice(0, 16))
  }, [startTimestamp])
  return {endDateInputRef, startDateInputRef}
}

const useGetSettingsTabLabelAndComponents = () => {
  const {identity: {isImpersonatedUser} = {}} = useGetIdentity()
  const translate = useTranslate()
  return useMemo<(string | any)[][]>(
    () => ([
      [translate('dialogs.channelSettings.general.title'), GeneralSettingsPanel],
      [
        translate('dialogs.channelSettings.workingHours.title'),
        ChannelWorkingHoursPanel,
      ],
      [translate('dialogs.channelSettings.autoReplies.title'), AutoRepliesTabPanel],
      [
        translate('dialogs.channelSettings.whatsappAccountBusinessProfile.title'),
        ChannelWhatsappAccountBusinessProfileEditPanel,
      ],
      ...(isImpersonatedUser ? [["WhatsApp-Import", WhatsappChatImportPanel]] : []),
    ]),
  [isImpersonatedUser, translate]
  )
}

const AUTOREPLY_RESPONSE_TYPES = ['TEXT', 'TEMPLATE']

const useStyles = makeStyles(theme => ({
  alert: {
    '& .MuiAlert-message': {
      paddingBottom: theme.typography.pxToRem(10),
      paddingTop: theme.typography.pxToRem(12),
    },
  },
  autoreplyMediaPreviewIcon: {
    height: theme.remSpacing(3),
    width: theme.remSpacing(3),
  },
  autoreplyToggle: {
    '& .Mui-error': {
      [theme.breakpoints.down('md')]: {
        top: theme.remSpacing(3.5),
      },
      flexDirection: 'row-reverse',
      justifyContent: 'space-between',
      position: 'absolute',
      right: theme.remSpacing(1),
      top: theme.remSpacing(.5),
    },
    '& .MuiCardContent-root': {
      padding: 0,
    },
    '& .MuiFormControlLabel-root': {
      '& .MuiFormControlLabel-label': {
        ...theme.typography.body1,
        color: theme.palette.text.secondary,
      },
      // moves label to left
      flexDirection: 'row-reverse',
      justifyContent: 'space-between',
      paddingLeft: theme.typography.pxToRem(20),
    },
    '& .MuiFormGroup-root': {
      margin: 0,
    },
    backgroundColor: 'transparent',
    boxShadow: 'none',
    paddingTop: '0 !important',
  },
  channelAbsenceDateTimeInputsRoot: {
    '& .MuiTypography-root': {
      paddingBottom: theme.remSpacing(1),
      paddingLeft: theme.remSpacing(3),
    },
    '& input': {
      cursor: 'pointer',
    },
    marginTop: theme.remSpacing(.5),
  },
  channelWhatsappQrCodeRoot: {
    '& .MuiGrid-justify-content-xs-space-between': {
      paddingLeft: 0,
    },
    '& .MuiIconButton-root': {
      [theme.breakpoints.down('md')]: {
        marginTop: theme.typography.pxToRem(2),
      },
      marginBottom: theme.typography.pxToRem(12),
      marginRight: theme.typography.pxToRem(12),
      marginTop: theme.typography.pxToRem(24),
    },
    '& .MuiTypography-root': {
      [theme.breakpoints.down('md')]: {
        marginBottom: theme.typography.pxToRem(13),
      },
      marginBottom: theme.typography.pxToRem(16),
      width: theme.typography.pxToRem(320),
    },
    '& canvas': {
      marginBottom: theme.typography.pxToRem(12),
    },
    marginBottom: theme.typography.pxToRem(10),
  },
  channelWorkingHoursEdit: {
    '& .MuiCardContent-root': {
      paddingBottom: 0,
      paddingTop: 0,
    },
    '& .MuiFilledInput-root': {
      paddingTop: theme.typography.pxToRem(10),
    },
    marginBottom: '0 !important',
  },
  channelWorkingHoursPanelRoot: {
    '& .MuiAlert-root': {
      marginBottom: theme.remSpacing(3),
    },
  },
  chatImportPanel: {
    '& .MuiButton-root': {
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiTextField-root': {
      marginBottom: theme.remSpacing(1),
      marginTop: theme.remSpacing(1),
    },
  },
  dialogContent: {
    [theme.breakpoints.down('md')]: {
      '&': {
        paddingTop: theme.remSpacing(4),
      },
      '& .MuiBox-root': {
        '& .edit-page:not(:last-child)': {
          marginBottom: theme.remSpacing(5),
        },
      },
      '& .MuiCardContent-root': {
        paddingBottom: 0,
        paddingTop: 0,
      },
    },
    overflowX: 'hidden',
    paddingBottom: 0,
    paddingTop: 0,
  },
  inputGrid: {
    paddingLeft: theme.remSpacing(1),
    paddingRight: theme.remSpacing(1),
  },
  isAbsenceHintBoxVisible: {
    display: 'block',
  },
  root: {
    [theme.breakpoints.up('lg')]: {
      '& .MuiDialog-paper': {
        position: 'fixed',
        top: theme.typography.pxToRem(5),
      },
    },
    '& .MuiDialog-paper': {
      height: '100%',
    },
  },
  timeInput: {
    '& .MuiFormHelperText-root': {
      marginTop: theme.typography.pxToRem(30),
      paddingTop: theme.typography.pxToRem(30),
      position: 'absolute',
      top: theme.remSpacing(1),
    },
    '&.MuiFormControl-root.MuiTextField-root': {
      width: '100%',
    },
  },
  workingHoursInputs: {
    '& .MuiFormControl-root': {
      marginTop: 0,
      [theme.breakpoints.down('sm')]: {
        marginLeft: theme.typography.pxToRem(12),
        width: theme.typography.pxToRem(80),
      },
    },
    '& .MuiFormControlLabel-root': {
      '& .MuiTypography-root': {
        color: theme.palette.text.secondary,
        fontSize: theme.typography.caption.fontSize,
      },
      display: 'block',
      fontSize: theme.typography.caption.fontSize,
    },
    '& .MuiFormGroup-root': {
      [theme.breakpoints.down('sm')]: {
        '& .MuiTypography-root': {
          marginLeft: 0,
        },
        marginTop: theme.typography.pxToRem(33),
        width: theme.typography.pxToRem(20),
      },
      display: 'flex',
      marginBottom: 0,
      marginTop: theme.typography.pxToRem(30),
      width: theme.typography.pxToRem(75),
    },
    '& .MuiGrid-item': {
      paddingLeft: 0,
    },
    '& .MuiTypography-root': {
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
      },
      marginRight: theme.remSpacing(1.5),
      marginTop: theme.typography.pxToRem(10),
      minWidth: theme.typography.pxToRem(75),
    },
    '& input': {
      cursor: 'pointer',
    },
    [theme.breakpoints.up('md')]: {
      height: theme.typography.pxToRem(100),
    },
    marginLeft: 0,
  },
}))

const CHANNEL_CHAT_BOT_USER_QUERY = gql`query ($channelId: uuid!) {
  channel_chat_bot_users(where: {channelId: {_eq: $channelId}}) {id}
}`

const CHANNEL_WORKING_HOURS_QUERY = gql`
  query($channelId: uuid!) {
    channel_working_hours(where: {channelId: {_eq: $channelId}}, order_by: {day: asc}) {
      closingTime
      day
      id
      isOpen
      openingTime
    }
  }
`

const IMPORT_WHATSAPP_CHAT_MUTATION = gql`
  mutation(
    $channelId: uuid!,
    $customerPhoneNumber: String!,
    $textFileContentHex: String!,
    $textFileMerchantUserName: String!,
  ){
    importWhatsappChat(
      channelId: $channelId,
      customerPhoneNumber: $customerPhoneNumber,
      textFileContentHex: $textFileContentHex,
      textFileMerchantUserName: $textFileMerchantUserName
    ){id}
  }
`

const WHATSAPP_ACCOUNTS_QUERY = gql`
  query($channelId: uuid) {
    whatsapp_accounts(where: {channel: {id: {_eq: $channelId}}}){id phoneNumber provider}
  }
`

interface ChannelSettingsDialogProps {
  channelId: string | undefined
  onClose: VoidFunction
  open?: boolean
}

interface HasChannelId {
  channelId: string
}

interface HasDisabled {
  disabled?: boolean
}

interface Alert {
  id: string
  severity: AlertColor
  text: string|JSX.Element
}

interface WorkingHoursInputsProps extends HasDisabled {
  [k: string]: any
  record: ChannelWorkingHours
}

interface ChannelChatBotSettingsProps extends HasChannelId, HasDisabled {
  channelChatBotUser: ChannelChatBotUsers
}

interface UseDateRangeInputValidationProps {
  endTimestamp: string
  startTimestamp: string
}

export default ChannelSettingsDialog
export {ChannelChatBotSettingsMessageInputs}
